import { DateTime } from "luxon";

export const formatDateAndTime = (
  date: Date,
  locale: string
): { formattedDate: string; formattedTime: string } => {
  const optionsDate: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "short",
    day: "numeric",
  };

  const optionsTime: Intl.DateTimeFormatOptions = {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  };

  const formattedDate = new Intl.DateTimeFormat(locale, optionsDate).format(
    date
  );
  const formattedTime = new Intl.DateTimeFormat(locale, optionsTime).format(
    date
  );

  return {
    formattedDate, //May 14, 2024
    formattedTime, //21:00
  };
};
export const formatDateTimeAgo = (date: Date, local: string): string => {
  const now = new Date();
  const diff = now.getTime() - date.getTime();
  const minutesAgo = Math.floor(diff / (1000 * 60));
  const options: Intl.DateTimeFormatOptions = {
    day: "numeric",
    month: "short",
    year: now.getFullYear() !== date.getFullYear() ? "numeric" : undefined,
    hour: "numeric",
    minute: "numeric",
  };
  if (minutesAgo === 0) return "just now";
  if (minutesAgo < 15) {
    return `${minutesAgo} min ago`;
  } else if (now.toDateString() !== date.toDateString()) {
    return date.toLocaleDateString(local, { day: "numeric", month: "short" });
  } else {
    return date.toLocaleTimeString(undefined, {
      hour: "numeric",
      minute: "numeric",
    });
  }
};

export const formatEducationPeriod = (
  startDate: Date,
  endDate: Date,
  locale: string = "en"
): string => {
  const options: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "long",
  };

  const formattedStartDate = new Intl.DateTimeFormat(locale, options).format(
    startDate
  );
  const formattedEndDate = new Intl.DateTimeFormat(locale, options).format(
    endDate
  );

  return `${formattedStartDate} - ${formattedEndDate}`; //May 2020 - July 2022
};

export const formatTimeZoneWithGMT = (timeZone: string): string => {
  const now = new Date();

  const options: Intl.DateTimeFormatOptions = {
    timeZone,
    timeZoneName: "short",
    hour: "2-digit",
    minute: "2-digit",
  };

  const formatter = new Intl.DateTimeFormat("en-US", options);
  const parts = formatter.formatToParts(now);
  const timeZoneName = parts.find(
    (part) => part.type === "timeZoneName"
  )?.value;

  return `${timeZone} (${timeZoneName})`;
};

export const getDurationString = (
  from: Date,
  to: Date = new Date(),
  locale: string = "en"
) => {
  const start = DateTime.fromJSDate(from);
  const end = DateTime.fromJSDate(to);

  const diff = end.diff(start, ["years", "months"]).toObject();

  const years = Math.floor(diff.years || 0);
  const months = Math.floor(diff.months || 0);

  const formatWord = (value: number, words: [string, string, string]) => {
    const mod10 = value % 10;
    const mod100 = value % 100;

    if (locale === "en") {
      return value === 1 ? words[0] : words[1];
    }

    if (mod10 === 1 && mod100 !== 11) return words[0];
    if ([2, 3, 4].includes(mod10) && ![12, 13, 14].includes(mod100))
      return words[1];
    return words[2];
  };

  const localized: Record<
    "year" | "month",
    Record<string, [string, string, string]>
  > = {
    year: {
      ru: ["год", "года", "лет"],
      uk: ["рік", "роки", "років"],
      en: ["year", "years", "years"],
    },
    month: {
      ru: ["месяц", "месяца", "месяцев"],
      uk: ["місяць", "місяці", "місяців"],
      en: ["month", "months", "months"],
    },
  };

  const parts: string[] = [];

  if (years > 0) {
    const word = formatWord(years, localized.year[locale]);
    parts.push(`${years} ${word}`);
  }

  if (months > 0 || years === 0) {
    const word = formatWord(months, localized.month[locale]);
    parts.push(`${months} ${word}`);
  }

  return {
    years,
    months,
    text: parts.join(" "),
  };
};
